import { useTranslation } from 'react-i18next';

/**
 * Tiny DA / EN segmented toggle for the header. Two mono uppercase
 * labels separated by a hairline rule — the active one is ink, the
 * inactive one is muted slate. Matches the bureaucratic register
 * chrome (no dropdown, no flag emojis, no globe icon).
 *
 * The selected language persists to localStorage under ``lexta-lang``
 * via i18next-browser-languagedetector — see ``i18n/config.ts``.
 */
export function LanguageToggle() {
  const { i18n } = useTranslation();
  const current = (i18n.resolvedLanguage || i18n.language || 'da')
    .slice(0, 2)
    .toLowerCase();

  const handleSelect = (lng: 'da' | 'en') => {
    if (lng === current) return;
    void i18n.changeLanguage(lng);
  };

  return (
    <div
      role="group"
      aria-label="Language"
      className="inline-flex items-center font-mono text-[11px] tracking-[0.12em] uppercase"
    >
      <LanguageButton
        code="da"
        active={current === 'da'}
        onClick={() => handleSelect('da')}
      />
      <span
        aria-hidden="true"
        className="mx-2 h-3 w-px bg-[var(--color-rule)]"
      />
      <LanguageButton
        code="en"
        active={current === 'en'}
        onClick={() => handleSelect('en')}
      />
    </div>
  );
}

function LanguageButton({
  code,
  active,
  onClick,
}: {
  code: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'transition-colors duration-150 ' +
        (active
          ? 'text-[var(--color-primary)]'
          : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]')
      }
    >
      {code}
    </button>
  );
}
